// src/components/ui/ModelPipelineUI.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './card';
import { Settings, Mic, Brain, MessageSquare, Volume2, Users, User, ChevronDown, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription } from './alert';
import { Button } from './button';

export const ModelPipelineUI = () => {
  // Model configurations with parameters
  const modelConfigs = {
    sst: {
      'Whisper': {
        model_size: { type: 'select', options: ['tiny', 'base', 'small', 'medium', 'large'], default: 'base' },
        language: { type: 'select', options: ['en', 'fr', 'de', 'es', 'auto'], default: 'en' },
        beam_size: { type: 'number', min: 1, max: 10, default: 5 },
        temperature: { type: 'range', min: 0, max: 1, step: 0.1, default: 0.0 }
      },
      'DeepSpeech': {
        beam_width: { type: 'number', min: 1, max: 1024, default: 500 },
        lm_weight: { type: 'range', min: 0, max: 2, step: 0.1, default: 0.75 }
      }
    },
    emotion: {
      'BERT-Emotion': {
        threshold: { type: 'range', min: 0, max: 1, step: 0.01, default: 0.5 },
        batch_size: { type: 'number', min: 1, max: 64, default: 32 }
      },
      'RoBERTa-Emotion': {
        confidence_threshold: { type: 'range', min: 0, max: 1, step: 0.01, default: 0.7 },
        max_length: { type: 'number', min: 64, max: 512, default: 128 }
      }
    },
    llm: {
      'GPT-4': {
        temperature: { type: 'range', min: 0, max: 2, step: 0.1, default: 1.0 },
        max_tokens: { type: 'number', min: 1, max: 4096, default: 2048 },
        top_p: { type: 'range', min: 0, max: 1, step: 0.01, default: 0.9 }
      },
      'Claude': {
        temperature: { type: 'range', min: 0, max: 1, step: 0.1, default: 0.7 },
        max_tokens: { type: 'number', min: 1, max: 8192, default: 4096 }
      }
    },
    tts: {
      'Coqui': {
        speaker_id: { type: 'number', min: 0, max: 10, default: 0 },
        speed: { type: 'range', min: 0.5, max: 2, step: 0.1, default: 1.0 }
      },
      'Tacotron': {
        pitch: { type: 'range', min: 0.5, max: 2, step: 0.1, default: 1.0 },
        speaking_rate: { type: 'range', min: 0.5, max: 2, step: 0.1, default: 1.0 }
      }
    }
  };

  // States
  const [selectedModels, setSelectedModels] = useState({
    sst: 'Whisper',
    emotion: 'BERT-Emotion',
    llm: 'GPT-4',
    tts: 'Coqui'
  });

  const [modelParams, setModelParams] = useState({});
  const [configLevel, setConfigLevel] = useState('all');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [expandedPanels, setExpandedPanels] = useState({});
  const [isDefault, setIsDefault] = useState(false);

  // Initialize parameters with defaults
  useEffect(() => {
    const initParams = {};
    Object.entries(selectedModels).forEach(([stage, model]) => {
      if (modelConfigs[stage][model]) {
        initParams[stage] = {};
        Object.entries(modelConfigs[stage][model]).forEach(([param, config]) => {
          initParams[stage][param] = config.default;
        });
      }
    });
    setModelParams(initParams);
  }, []);

  // Sample data
  const users = [
    { id: '1', name: 'John Doe' },
    { id: '2', name: 'Jane Smith' }
  ];

  const groups = [
    { id: '1', name: 'Administrators' },
    { id: '2', name: 'Developers' }
  ];

  // Handle model selection change
  const handleModelChange = (stage, model) => {
    setSelectedModels(prev => ({
      ...prev,
      [stage]: model
    }));
    
    // Reset parameters to defaults for new model
    if (modelConfigs[stage][model]) {
      const newParams = {};
      Object.entries(modelConfigs[stage][model]).forEach(([param, config]) => {
        newParams[param] = config.default;
      });
      setModelParams(prev => ({
        ...prev,
        [stage]: newParams
      }));
    }
  };

  // Handle parameter change
  const handleParamChange = (stage, param, value) => {
    setModelParams(prev => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        [param]: value
      }
    }));
  };

  // Toggle parameter panel
  const togglePanel = (stage) => {
    setExpandedPanels(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
  };

  // Parameter Input Component
  const ParameterInput = ({ type, param, value, config, onChange }) => {
    switch (type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="p-2 border rounded-md w-full"
          >
            {config.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'range':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="range"
              min={config.min}
              max={config.max}
              step={config.step}
              value={value}
              onChange={(e) => onChange(parseFloat(e.target.value))}
              className="w-full"
            />
            <span className="text-sm w-12">{value.toFixed(2)}</span>
          </div>
        );
      case 'number':
        return (
          <input
            type="number"
            min={config.min}
            max={config.max}
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className="p-2 border rounded-md w-full"
          />
        );
      default:
        return null;
    }
  };

  // Pipeline Stage Component
  const PipelineStage = ({ stage, icon: Icon, title }) => {
    const currentModel = selectedModels[stage];
    const stageParams = modelParams[stage] || {};
    const modelOptions = Object.keys(modelConfigs[stage]);

    return (
      <div className="flex flex-col space-y-2 p-4 border rounded-lg bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon className="w-5 h-5 text-blue-500" />
            <h3 className="font-medium text-lg">{title}</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => togglePanel(stage)}
          >
            {expandedPanels[stage] ? 
              <ChevronDown className="w-4 h-4" /> : 
              <ChevronRight className="w-4 h-4" />
            }
          </Button>
        </div>

        <select 
          value={currentModel}
          onChange={(e) => handleModelChange(stage, e.target.value)}
          className="p-2 border rounded-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {modelOptions.map((model) => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>

        {expandedPanels[stage] && modelConfigs[stage][currentModel] && (
          <div className="mt-4 space-y-4">
            {Object.entries(modelConfigs[stage][currentModel]).map(([param, config]) => (
              <div key={param} className="space-y-1">
                <label className="text-sm font-medium">
                  {param.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </label>
                <ParameterInput
                  type={config.type}
                  param={param}
                  value={stageParams[param]}
                  config={config}
                  onChange={(value) => handleParamChange(stage, param, value)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Save configuration
  const handleSaveConfig = () => {
    const config = {
      level: configLevel,
      models: selectedModels,
      parameters: modelParams,
      isDefault,
      targetId: configLevel === 'individual' ? selectedUser : 
                configLevel === 'group' ? selectedGroup : 'all'
    };
    console.log('Saving configuration:', config);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Settings className="w-6 h-6 text-blue-500" />
          <span>AI Pipeline Configuration</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Configuration Level */}
          <div className="mb-6 p-4 border rounded-lg bg-white">
            <h3 className="font-medium mb-4">Configuration Level</h3>
            <div className="flex flex-col space-y-4">
              <div className="flex space-x-4">
                <Button
                  variant={configLevel === 'all' ? 'default' : 'secondary'}
                  onClick={() => setConfigLevel('all')}
                  className="flex items-center space-x-2"
                >
                  <Users className="w-4 h-4" />
                  <span>All Users</span>
                </Button>
                <Button
                  variant={configLevel === 'group' ? 'default' : 'secondary'}
                  onClick={() => setConfigLevel('group')}
                  className="flex items-center space-x-2"
                >
                  <Users className="w-4 h-4" />
                  <span>Group</span>
                </Button>
                <Button
                  variant={configLevel === 'individual' ? 'default' : 'secondary'}
                  onClick={() => setConfigLevel('individual')}
                  className="flex items-center space-x-2"
                >
                  <User className="w-4 h-4" />
                  <span>Individual</span>
                </Button>
              </div>

              {configLevel === 'individual' && (
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="p-2 border rounded-md"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              )}

              {configLevel === 'group' && (
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="p-2 border rounded-md"
                >
                  <option value="">Select Group</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Pipeline Stages */}
          <div className="grid grid-cols-1 gap-4">
            <PipelineStage
              stage="sst"
              icon={Mic}
              title="Speech-to-Text"
            />
            <PipelineStage
              stage="emotion"
              icon={Brain}
              title="Emotion Analysis"
            />
            <PipelineStage
              stage="llm"
              icon={MessageSquare}
              title="Language Model"
            />
            <PipelineStage
              stage="tts"
              icon={Volume2}
              title="Text-to-Speech"
            />
          </div>

          {/* Default Configuration Toggle */}
          <div className="flex items-center space-x-2 mt-4">
            <input
              type="checkbox"
              id="default-config"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="default-config" className="text-sm font-medium">
              Set as default configuration
            </label>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSaveConfig}
            className="w-full"
          >
            Save Configuration
          </Button>

          {/* Configuration Priority Alert */}
          <Alert>
            <AlertDescription>
              Configuration Priority: Individual > Group > All Users > Default
            </AlertDescription>
          </Alert>
        </div>
      </CardContent>
    </Card>
  );
};